import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Building2, MessageSquare } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import logo from '../images/logo.png';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm();

  const loginValue = watch('login');

  // Formatar CPF quando o valor mudar
  useEffect(() => {
    if (loginValue && typeof loginValue === 'string') {
      const value = loginValue.replace(/\D/g, '');
      if (value.length <= 11 && value.length > 0) {
        const formatted = value
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d)/, '$1.$2')
          .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        if (formatted !== loginValue) {
          setValue('login', formatted, { shouldValidate: false });
        }
      }
    }
  }, [loginValue, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    await login(data.login, data.senha);
    setLoading(false);
  };

  return (
    <div className="main-container flex items-center justify-center bg-primary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl border border-primary-100 px-8 py-10 space-y-8">
          <div className="mx-auto flex flex-col items-center justify-center space-y-3">
            <img
              src={logo}
              alt="Logo CondoSmart"
              className="h-20 w-auto drop-shadow"
            />
            <p className="text-center text-sm font-medium text-primary-600">
              Faça login para acessar o sistema
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="login" className="label">
                  CPF
                </label>
                <input
                  {...register('login', {
                    required: 'CPF é obrigatório',
                    pattern: {
                      value: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
                      message: 'CPF deve estar no formato 000.000.000-00'
                    }
                  })}
                  type="text"
                  className={errors.login ? 'input-error' : 'input'}
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
                {errors.login && (
                  <p className="mt-1 text-sm text-red-600">{errors.login.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="senha" className="label">
                  Senha
                </label>
                <div className="relative">
                  <input
                    {...register('senha', {
                      required: 'Senha é obrigatória',
                      minLength: {
                        value: 6,
                        message: 'Senha deve ter pelo menos 6 caracteres'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className={errors.senha ? 'input-error pr-10' : 'input pr-10'}
                    placeholder="Sua senha"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.senha && (
                  <p className="mt-1 text-sm text-red-600">{errors.senha.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex justify-center items-center shadow-lg shadow-primary-200/60"
              >
                {loading ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : null}
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </div>

            <div className="text-center space-y-3 text-primary-600">
              <div>
                <button
                  type="button"
                  className="text-sm font-semibold text-primary-600 hover:text-primary-500 transition-colors"
                  onClick={() => navigate('/esqueceu-senha')}
                >
                  Esqueceu a Senha?
                </button>
              </div>
              
              <div>
              <p className="text-sm text-primary-500">
                  Não tem uma conta?{' '}
                  <button
                    type="button"
                    className="font-semibold text-accent-500 hover:text-accent-400 transition-colors"
                    onClick={() => navigate('/cadastro')}
                  >
                    Cadastre-se
                  </button>
                </p>
              </div>
              
              <div>
                <button
                  type="button"
                  className="inline-flex items-center text-sm font-medium text-primary-500 hover:text-primary-400 transition-colors"
                  onClick={() => navigate('/fale-conosco')}
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Fale Conosco
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
